import { FC, useContext, useMemo } from 'react';

import { Preloader, Label } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import s from './HostsField.module.scss';
import { IFormValues, IHostsFieldProps } from './interfaces';
import { getConfig } from './utils';

export const HostsField: FC<IHostsFieldProps> = ({
  value,
  onChange,
  hostGroupsTitlesById,
  hosts,
  loading,
  currentBooking,
  tariffs,
}) => {
  const { t } = useContext(LocalizationContext);
  const formContext = useFormContext<IFormValues>();

  const currentDateTime = dayjs().format('YYYY-MM-DD HH:mm:ss');

  const duration = tariffs ? 0 : formContext?.watch('duration') || 0;
  const startDate = tariffs
    ? currentDateTime
    : formContext?.watch('date') || '';
  const startTime = tariffs
    ? currentDateTime
    : formContext?.watch('time') || '';

  const bookingStart = useMemo(() => {
    if (!startDate || !startTime) return undefined;

    const [hours, minutes] = startTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return undefined;

    return dayjs(startDate).hour(hours).minute(minutes);
  }, [startDate, startTime]);

  const bookingEnd = useMemo(() => {
    if (!bookingStart || !duration) return undefined;

    return bookingStart.add(duration, 'hours');
  }, [bookingStart, duration, startDate, startTime]);

  const sortedHosts = useMemo(
    () =>
      [...(hosts || [])]?.sort(
        (
          { position: p1 = 0, group_id: g1 },
          { position: p2 = 0, group_id: g2 }
        ) => {
          if (g1 === g2) {
            return p1 - p2;
          }

          return g1 - g2;
        }
      ),
    [hosts]
  );

  return (
    <div className={s.hosts}>
      <Preloader loading={loading}>
        {sortedHosts?.map((host) => {
          const config = getConfig({
            host,
            startTime: bookingStart && dayjs(bookingStart),
            endTime: bookingEnd && dayjs(bookingEnd),
            currentBooking,
            t,
          });
          return (
            <div
              key={`${host.id}-${host.alias}`}
              className={cn(
                s.host,
                config.disabled && s.disabled,
                value.includes(host.id) && s.selected
              )}
              onClick={() => {
                if (value.includes(host.id)) {
                  return onChange(value.filter((hostId) => hostId !== host.id));
                }

                if (config.disabled) return;

                onChange([...value, host.id]);
              }}
            >
              <div className={s.data}>
                {host.alias}
                <span className={s['host-group']}>
                  {hostGroupsTitlesById?.[host.group_id] ??
                    t('booking.host.group.placeholder')}
                </span>
              </div>
              <Label {...config.labelProps} />
            </div>
          );
        })}
      </Preloader>
    </div>
  );
};
